import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Download, Share2, Eye, Edit, Sparkles, FileText, Camera, Palette, Trash2, Image as ImageIcon, Printer, Database, FileSignature, X, Loader2, Copy, ClipboardCheck, PenTool } from 'lucide-react';
import resumeAPI, { generatePublicLinkId } from '../services/api';
import { DEFAULT_RESUME, RESUME_TEMPLATES, SENIORITY_LEVELS, INDUSTRIES, HIGHLIGHT_COLORS } from '../config/constants';
import TemplateSelector from '../components/templates/TemplateSelector';
import ResumeEditor from '../components/ResumeEditor';
import ProfilePictureFrame from '../components/ProfilePictureFrame';
import ImageEditorModal from '../components/ImageEditorModal';
import ContentVisibilityDropdown from '../components/ContentVisibilityDropdown';
import EditableResumePreview from '../components/templates/EditableResumePreview';
import platformAnalytics from '../services/platformAnalytics';
import { generateCoverLetter } from '../services/aiService';
import { FONT_SIZE_SECTION_KEYS } from '../utils/fontSizeHelper';

const ResumeBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resume, setResume] = useState(DEFAULT_RESUME);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [message, setMessage] = useState(null);
  const [coreSkills, setCoreSkills] = useState([]);
  const [allResumes, setAllResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState(id || null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageType, setEditingImageType] = useState(null);
  const [customFrameText, setCustomFrameText] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [hasGeneratedLink, setHasGeneratedLink] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCoverLetterModal, setShowCoverLetterModal] = useState(false);
  const [coverLetterText, setCoverLetterText] = useState('');
  const [coverLetterJobDesc, setCoverLetterJobDesc] = useState('');
  const [coverLetterGenerating, setCoverLetterGenerating] = useState(false);
  const [coverLetterCopied, setCoverLetterCopied] = useState(false);
  const [coverLetterError, setCoverLetterError] = useState('');
  const printRestoreTabRef = useRef(null);
  const importInputRef = useRef(null);
  const coreSkillsResumeIdRef = useRef(null);

  const formatTemplateLabel = (value) => {
    const raw = String(value || '');
    if (raw === 'import-custom') return 'Import (Custom)';
    const cleaned = raw.replace(/^json-/, '').replace(/-/g, ' ').trim();
    const title = cleaned
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return raw.startsWith('json-') ? `${title} (JSON)` : title;
  };

  const isImportedResume = !!(resume.import_source === 'file' && resume.imported_at);

  useEffect(() => {
    loadAllResumes();
  }, []);

  // Track page view for platform analytics
  useEffect(() => {
    platformAnalytics.startPageTracking('page_view_resume_builder', '/builder');
    
    return () => {
      platformAnalytics.stopPageTracking();
    };
  }, []);

  // Debug log when allResumes changes
  useEffect(() => {
    if (allResumes.length > 0) {
      console.log('🔍 [Resume Selector Debug] All resumes data:', allResumes.map(r => ({
        id: r.id || r._id || r.resume_id,
        title: r.title,
        is_primary: r.is_primary,
        import_source: r.import_source,
        imported_at: r.imported_at,
        hasImportMarkers: r.import_source === 'file' && r.imported_at
      })));
    }
  }, [allResumes]);

  useEffect(() => {
    if (id) {
      loadResume();
    }
  }, [id]);

  const loadAllResumes = async () => {
    try {
      const resumes = await resumeAPI.fetchAll({ full: true });
      console.log('[ResumeBuilder] Loaded resumes for selection:', resumes.length);
      setAllResumes(resumes);
    } catch (error) {
      console.error('Failed to load resumes:', error);
      showMessage('Failed to load resumes list. Please refresh the page.', 'error');
    }
  };

  const loadResume = async () => {
    setLoading(true);
    setCoreSkills([]);
    coreSkillsResumeIdRef.current = null;
    console.log('[ResumeBuilder] Loading resume with ID:', id);
    try {
      const data = await resumeAPI.fetchById(id);
      console.log('[ResumeBuilder] Resume loaded successfully (DIRECT from AWS):', {
        id: data?.id || data?._id,
        title: data?.title,
        is_primary: data?.is_primary,
        import_source: data?.import_source,
        imported_at: data?.imported_at
      });
      if (!data || (!data.id && !data._id)) {
        throw new Error('Resume not found or invalid data returned');
      }
      
      // If this is the primary resume, sync profile picture and banner from Users table
      if (data.is_primary) {
        try {
          const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim();
          if (AWS_API_BASE_URL) {
            const getToken = () => {
              try {
                const u = JSON.parse(localStorage.getItem('user') || '{}');
                return u?.tokens?.idToken || u?.idToken || '';
              } catch {
                return '';
              }
            };
            const token = getToken();
            const awsUrl = (path) => {
              const base = AWS_API_BASE_URL.replace(/\/+$/, '');
              const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
              return `${base}${p}`;
            };
            const res = await fetch(awsUrl('/me/profile'), {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              }
            });
            if (res.ok) {
              const profileData = await res.json();
              const profile = profileData?.profile;
              if (profile) {
                // Sync photo and banner from Users table to resume
                if (profile.profilePictureKey || profile.photo_url) {
                  data.photo_url = profile.profilePictureKey || profile.photo_url || data.photo_url;
                }
                if (profile.bannerPictureKey || profile.banner_url || profile.bannerUrl) {
                  data.banner_url = profile.bannerPictureKey || profile.banner_url || profile.bannerUrl || data.banner_url;
                }
              }
            }
          }
        } catch (e) {
          console.warn('Failed to sync pictures from Users table:', e);
        }
      }
      
      const inner = data.resume || {};
      const normalizedData = {
        ...data,
        ...inner,
        photo_url: inner.photo_url || data.photo_url || '',
        banner_url: inner.banner_url || data.banner_url || '',
        headline: inner.headline || data.headline || '',
        credentials: inner.credentials || data.credentials || '',
        contact_email: inner.contact_email || data.contact_email || '',
        linkedin_url: inner.linkedin_url || data.linkedin_url || '',
        __loadedAt: Date.now()
      };
      setResume(normalizedData);
      setCoreSkills(normalizedData.core_skills || []);
      coreSkillsResumeIdRef.current = id;
      setSelectedResumeId(id);
    } catch (error) {
      console.error('[ResumeBuilder] Failed to load resume:', error);
      console.error('[ResumeBuilder] Error details:', {
        message: error.message,
        status: error.status,
        resumeId: id
      });
      // Don't show error yet - try fallback first
      // Only show error if fallback also fails (see line 168)
      // Try to load from all resumes as fallback
      try {
        console.error('⚠️ [ResumeBuilder] PRIMARY FETCH FAILED - Attempting fallback');
        console.error('⚠️ [ResumeBuilder] Primary error was:', error.message);
        const allResumes = await resumeAPI.fetchAll({ full: true });
        const targetResume = allResumes.find(r => (r.id || r._id) === id);
        if (targetResume) {
          console.warn('⚠️ [ResumeBuilder] FALLBACK MODE ACTIVE - Using fetchAll() instead of direct fetch');
          console.warn('⚠️ [ResumeBuilder] Data structure before normalization:', targetResume);
          
          // Ensure data structure matches direct fetch
          // Flatten nested resume object to prevent data loss
          const innerFb = targetResume.resume || {};
          const normalizedResume = {
            ...targetResume,
            ...innerFb,
            photo_url: innerFb.photo_url || targetResume.photo_url || '',
            banner_url: innerFb.banner_url || targetResume.banner_url || '',
            headline: innerFb.headline || targetResume.headline || '',
            credentials: innerFb.credentials || targetResume.credentials || '',
            contact_email: innerFb.contact_email || targetResume.contact_email || '',
            linkedin_url: innerFb.linkedin_url || targetResume.linkedin_url || '',
            __loadedAt: Date.now()
          };
          
          console.warn('⚠️ [ResumeBuilder] Data structure after normalization:', normalizedResume);
          setResume(normalizedResume);
          setCoreSkills(normalizedResume.core_skills || []);
          coreSkillsResumeIdRef.current = id;
          setSelectedResumeId(id);
          // Fallback successful - no need to alarm the user
          console.log('[ResumeBuilder] Resume loaded successfully via fetchAll() fallback');
        } else {
          console.error('[ResumeBuilder] Resume not found in fallback');
          showMessage('Resume not found. Please try again from My Resumes.', 'error');
        }
      } catch (fallbackError) {
        console.error('[ResumeBuilder] Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResumeChange = (event) => {
    const resumeId = event.target.value;
    if (resumeId && resumeId !== 'new') {
      navigate(`/builder/${resumeId}`);
    } else if (resumeId === 'new') {
      navigate('/builder');
      setResume(DEFAULT_RESUME);
      setCoreSkills([]);
      setSelectedResumeId(null);
    }
  };

  const handleSave = async () => {
    if (!resume?.__loadedAt) {
      showMessage('Resume is still loading. Please wait before saving.', 'error');
      return;
    }
    setSaving(true);
    console.log('💾 [Save] Starting save process...', {
      resumeId: id,
      is_primary: resume?.is_primary,
      title: resume?.title,
      import_source: resume?.import_source,
      imported_at: resume?.imported_at
    });
    
    try {
      const safeCoreSkills = (coreSkillsResumeIdRef.current === id) ? coreSkills : (resume.core_skills || []);
      const resumeData = {
        ...resume,
        core_skills: safeCoreSkills
      };
      // Remove import_source and imported_at when saving from editor
      // These should only be set during actual import operations
      delete resumeData.import_source;
      delete resumeData.imported_at;
      
      console.log('💾 [Save] Resume data prepared (import markers removed)');
      
      if (id) {
        console.log('💾 [Save] Calling resumeAPI.update()...');
        const result = await resumeAPI.update(id, resumeData);
        console.log('💾 [Save] Update result:', result);
        
        // Sync picture & banner to Users table so profile page and Find People display them
        if (resumeData.is_primary) {
          try {
            console.log('💾 [Save] Syncing to Users table (primary resume)...');
            await resumeAPI.saveProfileToUsersTable(resumeData);
            console.log('💾 [Save] Users table sync completed');
          } catch (profileErr) {
            console.error('💾 [Save] Profile sync failed:', profileErr);
            const msg = profileErr?.message || profileErr?.error || 'Profile sync failed';
            showMessage(`Resume saved, but profile picture/banner did not sync to your profile: ${msg}. Try saving again or use an image URL.`, 'error');
          }
        }
        
        console.log('💾 [Save] ✅ Save completed successfully');
        showMessage('Resume updated successfully!', 'success');
        
        // Update load timestamp to prevent auto-reload from overwriting
        setResume(prev => ({ ...prev, __loadedAt: Date.now() }));
      } else {
        const newResume = { ...resumeData };
        if (!newResume.public_link_id) {
          newResume.public_link_id = generatePublicLinkId();
        }
        console.log('💾 [Save] Creating new resume...');
        const data = await resumeAPI.create(newResume);
        console.log('💾 [Save] ✅ Create completed:', data);
        showMessage('Resume created successfully!', 'success');
        navigate(`/builder/${data.id || data._id}`);
      }
    } catch (error) {
      console.error('💾 [Save] ❌ Save failed:', error);
      showMessage('Failed to save resume', 'error');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handleAfterPrint = () => {
      setIsPrinting(false);
      const previous = printRestoreTabRef.current;
      if (previous && previous !== 'preview') {
        setActiveTab(previous);
      }
      printRestoreTabRef.current = null;
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  // Listen for profile updates and reload primary resume data
  useEffect(() => {
    const checkProfileUpdates = async () => {
      const lastProfileUpdate = localStorage.getItem('profile_last_updated');
      if (!lastProfileUpdate) return;
      
      const updateTime = parseInt(lastProfileUpdate, 10);
      const loadTime = resume?.__loadedAt || 0;
      
      // If profile was updated after this resume was loaded, reload it
      if (updateTime > loadTime && resume?.is_primary && id) {
        console.log('[ResumeBuilder] Profile was updated, reloading primary resume data...');
        try {
          const freshData = await resumeAPI.fetchResume(id);
          if (freshData) {
            setResume({ ...freshData, __loadedAt: Date.now() });
            showMessage('Resume updated with latest profile changes!', 'success');
          }
        } catch (error) {
          console.error('[ResumeBuilder] Failed to reload after profile update:', error);
        }
      }
    };
    
    // Check on mount and every 2 seconds while page is visible
    checkProfileUpdates();
    const interval = setInterval(checkProfileUpdates, 2000);
    return () => clearInterval(interval);
  }, [id, resume?.is_primary, resume?.__loadedAt]);

  const getResumeId = () => id || selectedResumeId || resume?.id || resume?._id;
  const buildPublicResumeLink = (resumeId) =>
    `${window.location.origin}/resume-preview/${encodeURIComponent(resumeId)}/`;

  const copyResumeLink = async (resumeId, successMessage) => {
    const url = buildPublicResumeLink(resumeId);
    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      console.warn('Clipboard copy failed:', error);
    }
    showMessage(successMessage, 'success');
  };

  const handleGenerateResumeLink = async () => {
    if (isGeneratingLink) return;
    const resumeId = getResumeId();
    if (!resumeId) {
      showMessage('Please save your resume first to generate a link.', 'error');
      return;
    }
    setIsGeneratingLink(true);
    await copyResumeLink(resumeId, 'Resume link generated and copied!');
    setHasGeneratedLink(true); // Enable share button
    setIsGeneratingLink(false);
  };

  const handlePrintResume = async () => {
    if (isPrinting) return;
    const resumeId = getResumeId();
    if (!resumeId) {
      showMessage('Please save your resume first before downloading.', 'error');
      return;
    }
    setIsPrinting(true);
    try {
      console.log('[PDF Export] Starting PDF export for resume:', resumeId, 'template:', resume?.template);
      const result = await resumeAPI.exportResumePdf(resumeId, {
        template: resume?.template || RESUME_TEMPLATES.MODERN,
        useCache: true
      });
      console.log('[PDF Export] Result:', result);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
        showMessage('PDF generated successfully!', 'success');
      } else {
        console.error('[PDF Export] No URL in response:', result);
        showMessage('PDF export failed. Please try again.', 'error');
      }
    } catch (error) {
      console.error('[PDF Export] Exception:', error);
      console.error('[PDF Export] Error details:', error.message, error.stack);
      showMessage(`PDF export failed: ${error.message}`, 'error');
    } finally {
      setIsPrinting(false);
    }
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || '';
        const base64 = String(result).split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImportDocx = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setIsImporting(true);
    try {
      const base64 = await readFileAsBase64(file);
      const result = await resumeAPI.importResumeDocx({
        docxBase64: base64,
        template: resume?.template || RESUME_TEMPLATES.MODERN
      });
      const newId = result?.resume?.resume_id || result?.resume?.id || result?.resume?._id;
      if (newId) {
        showMessage('Resume imported successfully!', 'success');
        navigate(`/builder/${newId}`);
      } else {
        showMessage('Import completed, but resume ID was not returned.', 'error');
      }
    } catch (error) {
      console.error('DOCX import failed:', error);
      showMessage('DOCX import failed. Please try again.', 'error');
    } finally {
      setIsImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  };

  const handleImportClick = () => {
    if (isImporting) return;
    importInputRef.current?.click();
  };

  const handleGenerateCoverLetter = async () => {
    setCoverLetterGenerating(true);
    setCoverLetterError('');
    setCoverLetterText('');
    try {
      const result = await generateCoverLetter(resume, coverLetterJobDesc);
      setCoverLetterText(result);
    } catch (err) {
      setCoverLetterError(err.message || 'Failed to generate cover letter');
    } finally {
      setCoverLetterGenerating(false);
    }
  };

  const handleCopyCoverLetter = async () => {
    try {
      await navigator.clipboard.writeText(coverLetterText);
      setCoverLetterCopied(true);
      setTimeout(() => setCoverLetterCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = coverLetterText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCoverLetterCopied(true);
      setTimeout(() => setCoverLetterCopied(false), 2000);
    }
  };

  const handleDownloadCoverLetter = () => {
    const blob = new Blob([coverLetterText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Cover_Letter_${(resume.title || 'Resume').replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrintResumeButton = () => {
    window.print();
  };

  const handleOpenHiddenPreview = () => {
    const resumeId = getResumeId();
    if (!resumeId) {
      showMessage('Please save your resume first before previewing.', 'error');
      return;
    }
    
    // Cache current editor state to localStorage for preview
    const previewKey = `resume_preview_${Date.now()}`;
    try {
      const payload = {
        createdAt: Date.now(),
        resume: resume  // Current editor state with unsaved changes
      };
      localStorage.setItem(previewKey, JSON.stringify(payload));
    } catch (storageError) {
      console.warn('Failed to cache preview data:', storageError);
    }
    
    // Open preview with preview key to load live data
    const url = `${window.location.origin}/resume-preview?resume_id=${encodeURIComponent(resumeId)}&preview_key=${encodeURIComponent(previewKey)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShare = async () => {
    const resumeId = getResumeId();
    if (!resumeId) {
      showMessage('Please save your resume first to share.', 'error');
      return;
    }
    await copyResumeLink(resumeId, 'Public link copied to clipboard!');
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const updateResume = (field, value) => {
    setResume(prev => ({ ...prev, [field]: value }));
    if (field === 'core_skills') {
      setCoreSkills(value || []);
    }
  };

  const handleImageFileSelect = async (type, file) => {
    if (!file || !file.type.startsWith('image/')) return;
    
    try {
      setUploading(true);
      showMessage('Uploading image...', 'info');
      
      const imageType = type === 'photo' ? 'avatar' : 'banner';
      const cloudFrontUrl = await resumeAPI.uploadImageToS3(file, imageType);
      
      if (type === 'photo') {
        updateResume('photo_url', cloudFrontUrl);
      } else {
        updateResume('banner_url', cloudFrontUrl);
      }
      
      showMessage('Image uploaded successfully', 'success');
    } catch (error) {
      console.error('Image upload failed:', error);
      showMessage('Failed to upload image. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEditImage = (type) => {
    setEditingImageType(type);
    setShowImageEditor(true);
  };

  const handleSaveEditedImage = (editedData) => {
    if (editingImageType === 'photo') {
      updateResume('photo_url', editedData.imageUrl);
    } else if (editingImageType === 'banner') {
      updateResume('banner_url', editedData.imageUrl);
    }
    setShowImageEditor(false);
    setEditingImageType(null);
  };

  const handleDeleteImage = (type) => {
    if (window.confirm(`Are you sure you want to delete this ${type === 'photo' ? 'profile picture' : 'banner'}?`)) {
      if (type === 'photo') {
        updateResume('photo_url', '');
      } else if (type === 'banner') {
        updateResume('banner_url', '');
      }
    }
  };

  const handleDeleteFrame = () => {
    if (window.confirm('Are you sure you want to remove the profile frame?')) {
      updateResume('profile_frame', '');
      setCustomFrameText('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading resume...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        {/* Message Banner */}
        {message && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Resume Builder & Hosting</h1>
              <p className="text-gray-600 mt-1">
                {id ? 'Edit your resume' : 'Create, build, and host your professional resume'}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowCoverLetterModal(true)}
                className="btn btn-outline inline-flex items-center space-x-2 text-purple-700 border-purple-300 hover:bg-purple-50"
              >
                <FileSignature className="w-5 h-5" />
                <span>AI Cover Letter</span>
              </button>
              <button
                onClick={handleShare}
                className="btn btn-outline inline-flex items-center space-x-2"
                disabled={!getResumeId() || !hasGeneratedLink}
              >
                <Share2 className="w-5 h-5" />
                <span>Share</span>
              </button>
              <button
                onClick={handlePrintResume}
                className="btn btn-secondary inline-flex items-center space-x-2"
                disabled={isPrinting}
              >
                <Download className="w-5 h-5" />
                <span>{isPrinting ? 'Generating PDF...' : 'Download PDF'}</span>
              </button>
              <button
                onClick={handlePrintResumeButton}
                className="btn btn-primary inline-flex items-center space-x-2"
                disabled={!getResumeId()}
              >
                <Printer className="w-5 h-5" />
                <span>Print Resume</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary inline-flex items-center space-x-2"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>

          {/* Resume Selectors - Side by Side */}
          {allResumes.length > 0 && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Created Resumes Selector - Left */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <FileText className="w-4 h-4" />
                    Select Created Resume to Edit
                  </label>
                  <select
                    value={selectedResumeId || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        navigate(`/builder/${e.target.value}`);
                      }
                    }}
                    className="select w-full"
                  >
                    <option value="">Choose a created resume...</option>
                    {allResumes
                      .filter(r => {
                        // Show ONLY created resumes (not imported)
                        // Imported resumes have import_source='file' AND imported_at timestamp
                        // Everything else is considered "created"
                        const hasImportMarkers = r.import_source === 'file' && r.imported_at;
                        return !hasImportMarkers;
                      })
                      .map((r) => {
                        const rid = r.id || r._id || r.resume_id;
                        return (
                          <option key={rid} value={rid}>
                            {r.title || 'Untitled Resume'}
                            {r.is_primary ? ' (Primary)' : ''}
                          </option>
                        );
                      })}
                  </select>
                </div>

                {/* Imported Resumes Selector - Right */}
                {allResumes.filter(r => r.import_source === 'file' && r.imported_at).length > 0 && (
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Database className="w-4 h-4" />
                      Select Imported Resume to Edit
                    </label>
                    <select
                      value={selectedResumeId || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          navigate(`/builder/${e.target.value}`);
                        }
                      }}
                      className="select w-full"
                    >
                      <option value="">Choose an imported resume...</option>
                      {allResumes
                        .filter(r => r.import_source === 'file' && r.imported_at)
                        .map((r) => {
                          const rid = r.id || r._id || r.resume_id;
                          return (
                            <option key={rid} value={rid}>
                              {r.title || 'Untitled Resume'} 
                              {r.imported_at ? ` (${new Date(r.imported_at).toLocaleDateString()})` : ''}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex items-center justify-between border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('edit')}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === 'edit'
                    ? 'border-b-2 border-primary-600 text-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Edit className="w-5 h-5 inline mr-2" />
                Edit
              </button>
              {(resume.template === RESUME_TEMPLATES.JSON_ELEGANT || resume.template === RESUME_TEMPLATES.IMPORT_CUSTOM) && (
                <button
                  onClick={() => setActiveTab('edit-preview')}
                  className={`px-6 py-3 font-medium transition-colors ${
                    activeTab === 'edit-preview'
                      ? 'border-b-2 border-primary-600 text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <PenTool className="w-5 h-5 inline mr-2" />
                  Edit Preview
                </button>
              )}
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === 'preview'
                    ? 'border-b-2 border-primary-600 text-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Eye className="w-5 h-5 inline mr-2" />
                Preview
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Content Visibility Dropdown - Left of Template */}
              <ContentVisibilityDropdown
                resume={resume}
                onUpdateVisibility={(hiddenContent) => updateResume('hidden_content', hiddenContent)}
              />
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Template:</label>
                <select
                  value={resume.template || RESUME_TEMPLATES.MODERN}
                  onChange={(e) => updateResume('template', e.target.value)}
                  className="select w-48"
                >
                  {Object.values(RESUME_TEMPLATES)
                    .filter((t) => t !== RESUME_TEMPLATES.IMPORT_CUSTOM || isImportedResume)
                    .map((template) => (
                    <option key={template} value={template}>
                      {formatTemplateLabel(template)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleOpenHiddenPreview}
                className="btn btn-outline inline-flex items-center space-x-2"
              >
                <Eye className="w-5 h-5" />
                <span>Open Preview</span>
              </button>
              <button
                onClick={handleGenerateResumeLink}
                className="btn btn-outline"
                disabled={!getResumeId() || isGeneratingLink}
              >
                {isGeneratingLink ? 'Generating...' : 'Generate Resume Link'}
              </button>
            </div>
          </div>
        </div>

        <input
          ref={importInputRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={handleImportDocx}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor Panel */}
          {activeTab === 'edit' && (
            <div className="lg:col-span-1 space-y-6">
              {/* Profile Picture & Banner Section - syncs to profile page when saved */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Camera className="w-5 h-5 text-primary-600" />
                  Profile Picture & Banner
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  These appear on your profile page. Save this resume to update your profile.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Profile Picture */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
                    <div className="space-y-4">
                      <div className="relative inline-block">
                        <div
                          className="absolute inset-0 rounded-full p-1.5 shadow-lg"
                          style={{
                            backgroundColor: resume.highlight_color || '#3b82f6',
                            padding: '6px'
                          }}
                        >
                          <div className="w-full h-full rounded-full bg-white" />
                        </div>
                        <ProfilePictureFrame
                          photoUrl={resume.photo_url}
                          frameOption={resume.profile_frame}
                          size="w-32 h-32"
                        />
                      </div>
                      <div className="flex gap-2">
                        {resume.photo_url && (
                          <button
                            onClick={() => handleEditImage('photo')}
                            className="btn btn-outline text-sm inline-flex items-center gap-1"
                          >
                            <ImageIcon className="w-4 h-4" />
                            Edit/Crop Photo
                          </button>
                        )}
                        {resume.photo_url && (
                          <button
                            onClick={() => handleDeleteImage('photo')}
                            className="btn btn-outline text-sm inline-flex items-center gap-1 text-red-600 hover:bg-red-50 border-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageFileSelect('photo', e.target.files[0])}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Or Enter Image URL</label>
                        <input
                          type="url"
                          value={resume.photo_url || ''}
                          onChange={(e) => updateResume('photo_url', e.target.value)}
                          className="input"
                          placeholder="https://example.com/your-photo.jpg"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Banner */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
                    <div className="space-y-4">
                      {resume.banner_url && (
                        <div className="relative h-32 rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={resume.banner_url}
                            alt="Banner"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex gap-2">
                        {resume.banner_url && (
                          <button
                            onClick={() => handleEditImage('banner')}
                            className="btn btn-outline text-sm inline-flex items-center gap-1"
                          >
                            <ImageIcon className="w-4 h-4" />
                            Edit/Crop Banner
                          </button>
                        )}
                        {resume.banner_url && (
                          <button
                            onClick={() => handleDeleteImage('banner')}
                            className="btn btn-outline text-sm inline-flex items-center gap-1 text-red-600 hover:bg-red-50 border-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Banner
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageFileSelect('banner', e.target.files[0])}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Or Enter Image URL</label>
                        <input
                          type="url"
                          value={resume.banner_url || ''}
                          onChange={(e) => updateResume('banner_url', e.target.value)}
                          className="input"
                          placeholder="https://example.com/banner-image.jpg"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Profile Frame Options */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Profile Frame</label>
                    {resume.profile_frame && (
                      <button
                        onClick={handleDeleteFrame}
                        className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        Remove Frame
                      </button>
                    )}
                  </div>
                  
                  {/* Job Seeking Options (Green) */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Job Seeking (Green)</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {updateResume('profile_frame', 'lookingtowork'); setCustomFrameText('');}}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                          resume.profile_frame === 'lookingtowork'
                            ? 'border-green-600 bg-green-50 text-green-900 shadow-lg shadow-green-500/50'
                            : 'border-gray-200 hover:border-green-300 text-gray-700'
                        }`}
                      >
                        LOOKING TO WORK
                      </button>
                      <button
                        onClick={() => {updateResume('profile_frame', 'exploringnewroles'); setCustomFrameText('');}}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                          resume.profile_frame === 'exploringnewroles'
                            ? 'border-green-600 bg-green-50 text-green-900 shadow-lg shadow-green-500/50'
                            : 'border-gray-200 hover:border-green-300 text-gray-700'
                        }`}
                      >
                        EXPLORING NEW ROLES
                      </button>
                      <button
                        onClick={() => {updateResume('profile_frame', 'jobseeking'); setCustomFrameText('');}}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                          resume.profile_frame === 'jobseeking'
                            ? 'border-green-600 bg-green-50 text-green-900 shadow-lg shadow-green-500/50'
                            : 'border-gray-200 hover:border-green-300 text-gray-700'
                        }`}
                      >
                        JOB SEEKING
                      </button>
                    </div>
                  </div>

                  {/* Hiring & Recruiting (Blue) */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Hiring & Recruiting (Blue)</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {updateResume('profile_frame', 'recruitingnow'); setCustomFrameText('');}}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                          resume.profile_frame === 'recruitingnow'
                            ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-lg shadow-blue-500/50'
                            : 'border-gray-200 hover:border-blue-300 text-gray-700'
                        }`}
                      >
                        RECRUITING NOW
                      </button>
                      <button
                        onClick={() => {updateResume('profile_frame', 'seekingtalent'); setCustomFrameText('');}}
                        className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                          resume.profile_frame === 'seekingtalent'
                            ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-lg shadow-blue-500/50'
                            : 'border-gray-200 hover:border-blue-300 text-gray-700'
                        }`}
                      >
                        SEEKING TALENT
                      </button>
                    </div>
                  </div>

                  {/* Custom Frame Input */}
                  <div className="mb-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Custom Frame (Blue)</p>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Enter custom text (max 16 characters)</label>
                      <input
                        type="text"
                        maxLength={16}
                        value={customFrameText}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase();
                          setCustomFrameText(val);
                          if (val.trim()) {
                            updateResume('profile_frame', `custom:${val}`);
                          } else {
                            updateResume('profile_frame', '');
                          }
                        }}
                        placeholder="YOUR TEXT HERE"
                        className="input text-xs uppercase"
                        style={{ color: '#1e3a8a', fontWeight: '600' }}
                      />
                      <p className="text-xs text-gray-400 mt-1">{customFrameText.length}/16 characters</p>
                    </div>
                  </div>

                  {/* No Frame Option */}
                  <div>
                    <button
                      onClick={() => {updateResume('profile_frame', ''); setCustomFrameText('');}}
                      className={`px-3 py-2 text-xs font-medium rounded-lg border-2 ${
                        !resume.profile_frame
                          ? 'border-gray-600 bg-gray-50 text-gray-900'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      NO FRAME
                    </button>
                  </div>
                </div>

                {/* Border Color */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Profile Border Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {HIGHLIGHT_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => updateResume('highlight_color', color.value)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          (resume.highlight_color || '#3b82f6') === color.value
                            ? 'border-gray-900 scale-110 ring-2 ring-gray-300'
                            : 'border-gray-300 hover:border-gray-500'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <div className="mt-2">
                    <input
                      type="text"
                      value={resume.highlight_color || '#3b82f6'}
                      onChange={(e) => updateResume('highlight_color', e.target.value)}
                      className="input w-32"
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>

                {/* Font Size Controls (non-primary resumes only) */}
                {!resume.is_primary && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Resume Font Size
                    </label>

                    <div className="mb-3">
                      <label className="block text-xs text-gray-500 mb-1">Entire Resume</label>
                      <div className="flex items-center gap-3">
                        <select
                          value={resume.font_size_global || ''}
                          onChange={(e) => updateResume('font_size_global', e.target.value ? Number(e.target.value) : null)}
                          className="input w-40 text-sm"
                        >
                          <option value="">Template Default</option>
                          {Array.from({ length: 13 }, (_, i) => i + 4).map(pt => (
                            <option key={pt} value={pt}>{pt}pt</option>
                          ))}
                        </select>
                        {resume.font_size_global && (
                          <span className="text-xs text-gray-400">{resume.font_size_global}pt applied globally</span>
                        )}
                      </div>
                    </div>

                    <details className="group">
                      <summary className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer font-medium">
                        Per-section overrides
                      </summary>
                      <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-100">
                        {FONT_SIZE_SECTION_KEYS.map(({ key, label }) => (
                          <div key={key} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-600 truncate flex-1">{label}</span>
                            <select
                              value={resume.font_size_sections?.[key] || ''}
                              onChange={(e) => {
                                const val = e.target.value ? Number(e.target.value) : undefined;
                                const updated = { ...(resume.font_size_sections || {}) };
                                if (val) { updated[key] = val; } else { delete updated[key]; }
                                updateResume('font_size_sections', Object.keys(updated).length > 0 ? updated : null);
                              }}
                              className="input w-28 text-xs py-1"
                            >
                              <option value="">Use Global</option>
                              {Array.from({ length: 13 }, (_, i) => i + 4).map(pt => (
                                <option key={pt} value={pt}>{pt}pt</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>

              <ResumeEditor resume={resume} updateResume={updateResume} hideAbout={true} />
            </div>
          )}

          {/* Edit Preview Panel (inline WYSIWYG) */}
          {activeTab === 'edit-preview' && (
            <div className="lg:col-span-2">
              <EditableResumePreview
                resume={resume}
                updateResume={updateResume}
                isImported={isImportedResume}
              />
            </div>
          )}

          {/* Preview Panel */}
          {activeTab !== 'edit-preview' && (
          <div className={activeTab === 'preview' ? 'lg:col-span-2' : 'lg:col-span-1'}>
            <div className="sticky top-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Preview</h2>
                  {resume.ai_score > 0 && (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-yellow-500" />
                      <span className="font-semibold text-gray-900">
                        AI Score: {resume.ai_score}/100
                      </span>
                    </div>
                  )}
                </div>
                <div id="print-resume">
                  <div id="resume-preview" className="border border-gray-200 rounded-lg overflow-hidden">
                    <TemplateSelector template={resume.template} resume={resume} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Image Editor Modal - Hide "Anyone" visibility button */}
      <ImageEditorModal
        isOpen={showImageEditor}
        onClose={() => {
          setShowImageEditor(false);
          setEditingImageType(null);
        }}
        imageUrl={editingImageType === 'photo' ? resume.photo_url : resume.banner_url}
        onSave={handleSaveEditedImage}
        title={editingImageType === 'photo' ? 'Edit Profile Picture' : 'Edit Banner'}
        showVisibility={false}
        imageType={editingImageType === 'photo' ? 'avatar' : 'banner'}
      />

      {/* AI Cover Letter Modal */}
      {showCoverLetterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FileSignature className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">AI Cover Letter Generator</h2>
                  <p className="text-xs text-gray-500">Powered by Hugging Face</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCoverLetterModal(false);
                  setCoverLetterError('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Job Description <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={coverLetterJobDesc}
                  onChange={(e) => setCoverLetterJobDesc(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="4"
                  placeholder="Paste the job description here to tailor the cover letter to a specific position..."
                />
              </div>
              <button
                onClick={handleGenerateCoverLetter}
                disabled={coverLetterGenerating}
                className="w-full py-2.5 px-4 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition-colors"
              >
                {coverLetterGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Cover Letter
                  </>
                )}
              </button>

              {coverLetterError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {coverLetterError}
                </div>
              )}

              {coverLetterText && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Generated Cover Letter</span>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCopyCoverLetter}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        {coverLetterCopied ? (
                          <>
                            <ClipboardCheck className="w-3.5 h-3.5 text-green-600" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" />
                            Copy
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleDownloadCoverLetter}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={coverLetterText}
                    onChange={(e) => setCoverLetterText(e.target.value)}
                    className="w-full p-4 border border-gray-300 rounded-lg text-sm font-serif leading-relaxed focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="16"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeBuilderPage;
